// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConnectionProviderIcon} from '@app/features/connection/components/ConnectionProviderIcon';
import {CONNECTION_PROVIDER_NAMES} from '@app/features/connection/utils/ConnectionProviders';
import type {StreamActivity} from '@app/features/gateway/types/GatewayPresenceTypes';
import {openExternalUrlWithWarning} from '@app/features/messaging/utils/ExternalLinkUtils';
import styles from '@app/features/presence/components/LiveStreamBadge.module.css';
import Presence from '@app/features/presence/state/Presence';
import {getStreamActivityUrl} from '@app/features/presence/utils/StreamActivityUrl';
import StreamerMode from '@app/features/streamer_mode/state/StreamerMode';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArrowSquareOutIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const LIVE_DESCRIPTOR = msg({
	message: 'Live',
	comment: 'Short pill on a user who is streaming right now on Twitch or Kick. One word, shown in capitals.',
});
const LIVE_ON_DESCRIPTOR = msg({
	message: 'Live on {platform}',
	comment: 'Tooltip on the live pill in member and voice lists. {platform} is Twitch or Kick.',
});
const WATCH_ON_DESCRIPTOR = msg({
	message: 'Watch on {platform}',
	comment: 'Profile link that opens the user’s live stream. {platform} is Twitch or Kick.',
});

interface LiveStreamBadgeProps {
	userId: string;
	// A list row is already a button, so its pill stays static; the profile carries the link.
	variant: 'row' | 'profile';
	className?: string;
	// The member list resolves it from its own row presence first; omitted, the presence store decides.
	activity?: StreamActivity | null;
}

export const LiveStreamBadge: React.FC<LiveStreamBadgeProps> = observer(
	({userId, variant, className, activity: resolvedActivity}) => {
		const {i18n} = useLingui();
		const activity = resolvedActivity !== undefined ? resolvedActivity : Presence.getStreamActivity(userId);
		if (!activity) return null;
		const platform = CONNECTION_PROVIDER_NAMES[activity.platform];
		const pill = (
			<span
				className={clsx(styles.pill, styles[activity.platform])}
				data-platform={activity.platform}
				data-flx="presence.live-stream-badge.pill"
			>
				<ConnectionProviderIcon
					type={activity.platform}
					size={12}
					data-flx="presence.live-stream-badge.provider-icon"
				/>
				{i18n._(LIVE_DESCRIPTOR)}
			</span>
		);
		if (variant === 'row') {
			return (
				<Tooltip
					text={i18n._(LIVE_ON_DESCRIPTOR, {platform})}
					position="top"
					data-flx="presence.live-stream-badge.tooltip"
				>
					<span className={clsx(styles.row, className)} data-flx="presence.live-stream-badge.row">
						{pill}
					</span>
				</Tooltip>
			);
		}
		const url = StreamerMode.shouldHidePersonalInformation ? null : getStreamActivityUrl(activity);
		const content = (
			<>
				{pill}
				<span className={styles.label} data-flx="presence.live-stream-badge.label">
					{i18n._(url ? WATCH_ON_DESCRIPTOR : LIVE_ON_DESCRIPTOR, {platform})}
				</span>
			</>
		);
		if (!url) {
			return (
				<div
					className={clsx(styles.profile, styles[activity.platform], className)}
					data-flx="presence.live-stream-badge.profile"
				>
					{content}
				</div>
			);
		}
		return (
			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				className={clsx(styles.profile, styles[activity.platform], className)}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					openExternalUrlWithWarning(url);
				}}
				data-flx="presence.live-stream-badge.profile-link.click"
			>
				{content}
				<ArrowSquareOutIcon
					size={remFromPx(16)}
					weight="bold"
					className={styles.arrow}
					data-flx="presence.live-stream-badge.arrow"
				/>
			</a>
		);
	},
);
